import React, { useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { Table, Tag, Button, Tooltip, Dropdown, Checkbox } from 'antd';
import { HolderOutlined, InfoCircleOutlined, DeleteOutlined, WarningOutlined } from '@ant-design/icons';
import stc from 'string-to-color';
import { setGroupDragData } from './planningAssign';

export function createSelectionStore() {
    const selected = new Set();
    const idListeners = new Map();
    const sizeListeners = new Set();

    const notifyId = id => {
        idListeners.get(id)?.forEach(fn => fn(selected.has(id)));
    };

    const notifySize = () => {
        const size = selected.size;
        sizeListeners.forEach(fn => fn(size));
    };

    return {
        has: id => selected.has(id),
        getAll: () => Array.from(selected),
        get size() {
            return selected.size;
        },
        toggle(id) {
            if (selected.has(id)) selected.delete(id);
            else selected.add(id);
            notifyId(id);
            notifySize();
        },
        setVisible(ids, checked) {
            for (const id of ids) {
                if (checked) {
                    if (!selected.has(id)) {
                        selected.add(id);
                        notifyId(id);
                    }
                } else if (selected.delete(id)) {
                    notifyId(id);
                }
            }
            notifySize();
        },
        clear(ids) {
            const toClear = ids || Array.from(selected);
            let changed = false;
            for (const id of toClear) {
                if (selected.delete(id)) {
                    notifyId(id);
                    changed = true;
                }
            }
            if (changed) notifySize();
        },
        replace(ids) {
            const next = new Set(ids);
            const changed = [];
            for (const id of Array.from(selected)) {
                if (!next.has(id)) {
                    selected.delete(id);
                    changed.push(id);
                }
            }
            for (const id of next) {
                if (!selected.has(id)) {
                    selected.add(id);
                    changed.push(id);
                }
            }
            changed.forEach(notifyId);
            if (changed.length) notifySize();
        },
        subscribe(id, fn) {
            if (!idListeners.has(id)) idListeners.set(id, new Set());
            idListeners.get(id).add(fn);
            return () => idListeners.get(id)?.delete(fn);
        },
        subscribeSize(fn) {
            sizeListeners.add(fn);
            return () => sizeListeners.delete(fn);
        },
    };
}

function selectedIdsForRow(store, recordId) {
    if (!store) return [recordId];
    const selected = store.getAll();
    return selected.includes(recordId) && selected.length > 1 ? selected : [recordId];
}

export function SelectionCount({ store }) {
    const [count, setCount] = useState(() => store.size);
    useEffect(() => store.subscribeSize(setCount), [store]);
    if (!count) return null;
    return <h3>{`${count} selected`}</h3>;
}

const RowSelectCheckbox = React.memo(function RowSelectCheckbox({ store, id, onRangeSelect, onSetAnchor }) {
    const [checked, setChecked] = useState(() => store.has(id));
    useEffect(() => store.subscribe(id, setChecked), [id, store]);
    return (
        <Checkbox
            checked={checked}
            onClick={e => {
                e.stopPropagation();
                if (e.shiftKey) {
                    e.preventDefault();
                    onRangeSelect(id);
                }
            }}
            onChange={e => {
                if (e.nativeEvent?.shiftKey) return;
                store.toggle(id);
                onSetAnchor(id);
            }}
            onMouseDown={e => e.stopPropagation()}
        />
    );
});

function SelectAllCheckbox({ store, ids }) {
    const [size, setSize] = useState(() => store.size);
    useEffect(() => store.subscribeSize(setSize), [store]);
    const allSelected = ids.length > 0 && ids.every(id => store.has(id));
    const someSelected = size > 0 && ids.some(id => store.has(id));
    return (
        <Checkbox
            checked={allSelected}
            indeterminate={someSelected && !allSelected}
            onChange={e => store.setVisible(ids, e.target.checked)}
        />
    );
}

const tagColorCache = new Map();

function tagColor(value) {
    let color = tagColorCache.get(value);
    if (!color) {
        color = stc(value);
        tagColorCache.set(value, color);
    }
    return color;
}

function bayAssignedCount(bay) {
    return (bay?.assigned || []).reduce((n, item) => n + (item?.data?.length || 0), 0);
}

function groupKeyTitle(key) {
    const parts = String(key).split('.');
    return parts[parts.length - 1] || key;
}

function collectGroupKeys(data) {
    const keys = [];
    data.forEach(item => {
        Object.keys(item?.group || {}).forEach(key => {
            if (!keys.includes(key)) keys.push(key);
        });
    });
    return keys;
}

export const AssignBayMenu = React.forwardRef(function AssignBayMenu({ bays, onAssign }, ref) {
    const [menu, setMenu] = useState(null);

    useImperativeHandle(
        ref,
        () => ({
            open(event, groupIds) {
                const ids = Array.isArray(groupIds) ? groupIds : [groupIds].filter(Boolean);
                setMenu({ x: event.clientX, y: event.clientY, groupIds: ids });
            },
        }),
        []
    );

    const selectedCount = menu?.groupIds?.length || 0;

    return (
        <Dropdown
            open={!!menu}
            trigger={['click']}
            onOpenChange={open => {
                if (!open) setMenu(null);
            }}
            getPopupContainer={() => document.body}
            menu={{
                className: 'pendency-assign-menu',
                items: [
                    {
                        key: '_header',
                        label: selectedCount > 1 ? `Assign ${selectedCount} groups to bay` : 'Assign to bay',
                        disabled: true,
                    },
                    ...(bays || []).map(bay => ({
                        key: bay.id,
                        label: `${bay.name} (${bayAssignedCount(bay)}/${bay.capacity ?? '–'})`,
                    })),
                ],
                onClick: ({ key }) => {
                    const groupIds = menu?.groupIds;
                    setMenu(null);
                    if (groupIds?.length) onAssign(groupIds, key);
                },
            }}
        >
            <div
                className="pendency-assign-menu-anchor"
                style={{
                    position: 'fixed',
                    left: menu?.x ?? -9999,
                    top: menu?.y ?? -9999,
                    width: 1,
                    height: 1,
                }}
            />
        </Dropdown>
    );
});

function PendencyGroupTable({
    data = [],
    setSkuModal,
    loading,
    draggable = true,
    onDelete,
    isRowValid,
    scrollY = 'calc(80vh - 56px)',
    emptyText = 'No pendency groups',
    onAssignContextMenu,
    selectable = false,
    selectionStore,
}) {
    const groupKeys = useMemo(() => collectGroupKeys(data), [data]);
    const visibleIds = useMemo(() => data.map(item => item.id), [data]);
    const showActions = !!(setSkuModal || onDelete || isRowValid);
    const canAssign = !!onAssignContextMenu;
    const wrapRef = useRef(null);
    const anchorIdRef = useRef(null);

    const getDisplayedIds = useCallback(() => {
        const rows = wrapRef.current?.querySelectorAll('.ant-table-tbody tr[data-row-key]');
        if (!rows?.length) return visibleIds;
        const ids = [];
        const seen = new Set();
        rows.forEach(row => {
            const id = row.getAttribute('data-row-key');
            if (id && !seen.has(id)) {
                seen.add(id);
                ids.push(id);
            }
        });
        return ids.length ? ids : visibleIds;
    }, [visibleIds]);

    const setAnchor = useCallback(id => {
        anchorIdRef.current = id;
    }, []);

    const selectRangeTo = useCallback(
        toId => {
            if (!selectionStore) return;
            const ids = getDisplayedIds();
            const fromId = anchorIdRef.current;
            if (!fromId) {
                selectionStore.replace([toId]);
                anchorIdRef.current = toId;
                return;
            }
            const from = ids.indexOf(fromId);
            const to = ids.indexOf(toId);
            if (from < 0 || to < 0) {
                selectionStore.setVisible([toId], true);
                return;
            }
            const start = Math.min(from, to);
            const end = Math.max(from, to);
            selectionStore.replace(ids.slice(start, end + 1));
        },
        [getDisplayedIds, selectionStore]
    );

    const columns = useMemo(
        () => [
            ...(selectable && selectionStore
                ? [
                    {
                        title: <SelectAllCheckbox store={selectionStore} ids={visibleIds} />,
                        key: 'select',
                        width: 36,
                        align: 'center',
                        render: (_, record) => (
                            <RowSelectCheckbox
                                store={selectionStore}
                                id={record.id}
                                onRangeSelect={selectRangeTo}
                                onSetAnchor={setAnchor}
                            />
                        ),
                    },
                ]
                : []),
            ...(draggable
                ? [
                    {
                        title: '',
                        key: 'drag',
                        width: 28,
                        align: 'center',
                        render: () => <HolderOutlined className="pendency-drag-handle" />,
                    },
                ]
                : []),
            ...groupKeys.map(key => ({
                title: groupKeyTitle(key),
                key,
                ellipsis: true,
                render: (_, record) => {
                    const value = record?.group?.[key];
                    if (value == null || value === '') {
                        return <span className="pendency-empty">—</span>;
                    }
                    return (
                        <Tag color={tagColor(value)} className="pendency-tag" title={value}>
                            {value}
                        </Tag>
                    );
                },
                sorter: (a, b) => String(a?.group?.[key] ?? '').localeCompare(String(b?.group?.[key] ?? '')),
            })),
            {
                title: 'EANs',
                key: 'count',
                width: 72,
                align: 'right',
                fixed: 'right',
                render: (_, record) => <span className="pendency-count">{record?.data?.length ?? 0}</span>,
                sorter: (a, b) => (a?.data?.length ?? 0) - (b?.data?.length ?? 0),
            },
            ...(showActions
                ? [
                    {
                        title: '',
                        key: 'action',
                        width: onDelete ? 88 : 36,
                        align: 'center',
                        fixed: 'right',
                        render: (_, record) => {
                            const valid = !isRowValid || isRowValid(record);
                            return (
                                <span className="pendency-actions">
                                    {!valid && (
                                        <Tooltip title="Should not be assigned to current Bay">
                                            <WarningOutlined className="pendency-invalid-icon" />
                                        </Tooltip>
                                    )}
                                    {setSkuModal && (
                                        <Tooltip title="View SKUs">
                                            <Button
                                                type="text"
                                                size="small"
                                                icon={<InfoCircleOutlined />}
                                                onMouseDown={e => e.stopPropagation()}
                                                onClick={e => {
                                                    e.stopPropagation();
                                                    setSkuModal({
                                                        flag: true,
                                                        details: { id: record.id, group: record.group, data: record.data },
                                                    });
                                                }}
                                            />
                                        </Tooltip>
                                    )}
                                    {onDelete && (
                                        <Tooltip title="Remove from Bay">
                                            <Button
                                                type="text"
                                                size="small"
                                                danger
                                                icon={<DeleteOutlined />}
                                                onMouseDown={e => e.stopPropagation()}
                                                onClick={e => {
                                                    e.stopPropagation();
                                                    onDelete(record);
                                                }}
                                            />
                                        </Tooltip>
                                    )}
                                </span>
                            );
                        },
                    },
                ]
                : []),
        ],
        [
            draggable,
            groupKeys,
            isRowValid,
            onDelete,
            selectRangeTo,
            selectable,
            selectionStore,
            setAnchor,
            setSkuModal,
            showActions,
            visibleIds,
        ]
    );

    const onRow = useCallback(
        record => ({
            draggable: !!draggable,
            onMouseDown:
                selectable && selectionStore
                    ? event => {
                        if (event.target.closest('button')) return;
                        if (event.shiftKey) {
                            event.preventDefault();
                            if (event.target.closest('.ant-checkbox, input, label')) return;
                            selectRangeTo(record.id);
                            return;
                        }
                        if (!event.target.closest('.ant-checkbox, input, label')) {
                            anchorIdRef.current = record.id;
                        }
                    }
                    : undefined,
            onDragStart: draggable
                ? event => {
                    if (event.shiftKey || event.target.closest('button, input, label, .ant-checkbox')) {
                        event.preventDefault();
                        return;
                    }
                    setGroupDragData(event, selectedIdsForRow(selectionStore, record.id));
                }
                : undefined,
            onContextMenu: canAssign
                ? event => {
                    event.preventDefault();
                    event.stopPropagation();
                    onAssignContextMenu(event, selectedIdsForRow(selectionStore, record.id));
                }
                : undefined,
        }),
        [canAssign, draggable, onAssignContextMenu, selectRangeTo, selectable, selectionStore]
    );

    return (
        <div
            ref={wrapRef}
            className={selectable ? 'pendency-group-table-wrap--selectable' : undefined}
            onContextMenu={canAssign ? event => event.preventDefault() : undefined}
        >
            <Table
                className={`pendency-group-table${draggable ? '' : ' pendency-group-table--static'}${selectable ? ' pendency-group-table--selectable' : ''
                    }`}
                size="small"
                bordered
                rowKey="id"
                pagination={false}
                loading={loading}
                dataSource={data}
                columns={columns}
                showSorterTooltip={false}
                scroll={{ x: 'max-content', ...(scrollY ? { y: scrollY } : {}) }}
                locale={{ emptyText }}
                rowClassName={record => (isRowValid && !isRowValid(record) ? 'pendency-row-invalid' : '')}
                onRow={onRow}
            />
        </div>
    );
}

export default React.memo(PendencyGroupTable);
